import { defineStore } from "pinia";
import type { RouterOutput } from "~/plugins/trpc";
import { useCountdown } from "@vueuse/core";
import { AdminappPrivilege } from "evangelist-db-schema/api";

type UserType = RouterOutput["admin"]["getCurrentUser"];

export const useUserStore = defineStore("user", () => {
  const user = ref<UserType>();
  const isSpoofing = computed(() => Boolean(user.value?.authUserId));

  const hasSuperpowers = ref(false);

  const fiveMinutes = shallowRef(5 * 60);

  const {
    start,
    reset,
    remaining: remainingSuperpowerTime,
  } = useCountdown(fiveMinutes, {
    onComplete() {
      disableSuperpowers();
    },
  });

  function activateSuperpowers() {
    if (user.value) {
      addHasSuperpowers(user.value);
      hasSuperpowers.value = true;
      start();
    }
  }

  function disableSuperpowers() {
    if (user.value) {
      removeHasSuperpowers(user.value);
      reset();
      hasSuperpowers.value = false;
    }
  }

  function setUser(u: UserType) {
    user.value = u;
    removeHasSuperpowers(user.value);
  }

  function removeHasSuperpowers(user: UserType) {
    user.userRoleAssignments.forEach((assignment) => {
      assignment.privileges = assignment.privileges.filter(
        (privilege) => privilege !== AdminappPrivilege.HasSuperpowers,
      );
    });
  }

  function addHasSuperpowers(user: UserType) {
    user.userRoleAssignments.forEach((assignment) => {
      if (!assignment.privileges.includes(AdminappPrivilege.HasSuperpowers)) {
        assignment.privileges.push(AdminappPrivilege.HasSuperpowers);
      }
    });
  }

  const formattedRemainingSuperpowerTime = computed(() => {
    const minutes = Math.floor(remainingSuperpowerTime.value / 60);
    const seconds = remainingSuperpowerTime.value % 60;

    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  });

  return {
    user,
    isSpoofing,
    hasSuperpowers,
    activateSuperpowers,
    disableSuperpowers,
    formattedRemainingSuperpowerTime,
    setUser,
  };
});
